import { useRef, useState, type ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { useI18n } from '../../lib/i18n/I18nProvider';
import type { StringKey } from '../../lib/i18n/strings';
import { newIntent, useApiAction, useApiList } from '../../lib/api/queries';
import type { Expo } from '../../lib/api/types';
import { Badge, Card, DataTable, Field, Ltr, PageTitle, Select } from '../../ui/primitives';
import { EmptyState, ErrorState, LoadingState } from '../../ui/states';
import {
  ExpoScheduleFields,
  schedulePayload,
  validateSchedule,
  type ScheduleValues,
} from './ExpoScheduleFields';

const EXPO_STATUSES = [
  '',
  'draft',
  'announced',
  'registration_open',
  'registration_closed',
  'live',
  'ended',
  'archived',
  'cancelled',
];

const EXPO_STATUS_KEYS: Record<string, StringKey> = {
  draft: 'expo.status.draft',
  announced: 'expo.status.announced',
  registration_open: 'expo.status.registration_open',
  registration_closed: 'expo.status.registration_closed',
  live: 'expo.status.live',
  ended: 'expo.status.ended',
  archived: 'expo.status.archived',
  cancelled: 'expo.status.cancelled',
};

export function expoStatusKey(status: string): StringKey | null {
  return EXPO_STATUS_KEYS[status] ?? null;
}

export function statusTone(status: string): 'neutral' | 'ok' | 'warn' | 'danger' {
  if (status === 'live' || status === 'published' || status === 'active' || status === 'accepted') return 'ok';
  if (status === 'cancelled' || status === 'taken_down' || status === 'rejected' || status === 'suspended') {
    return 'danger';
  }
  if (status === 'draft' || status === 'pending' || status === 'building') return 'warn';
  return 'neutral';
}

/** `ADM-02` — the expo register, and the create form. */
export function AdminExpos(): ReactElement {
  const { t, bilingual, formatDateTime } = useI18n();
  const statusLabel = (value: string): string => {
    const key = expoStatusKey(value);
    return key ? t(key) : value;
  };
  const [status, setStatus] = useState('');
  const expos = useApiList<Expo>('/v1/admin/expos', status ? { status } : undefined);
  const create = useApiAction<Expo>(['/v1/admin/expos']);

  const [slug, setSlug] = useState('');
  const [nameAr, setNameAr] = useState('');
  const [nameEn, setNameEn] = useState('');
  const [timezone, setTimezone] = useState('Africa/Cairo');
  const [expectedBoothCount, setExpectedBoothCount] = useState('10');
  const [schedule, setSchedule] = useState<ScheduleValues>({
    registrationOpens: '', eventStarts: '', eventEnds: '', automatic: true,
  });
  const [scheduleAttempted, setScheduleAttempted] = useState(false);
  const scheduleErrorRef = useRef<HTMLDivElement>(null);
  const scheduleErrors = validateSchedule(schedule, timezone);
  const hasScheduleErrors = Object.keys(scheduleErrors).length > 0;

  return (
    <div className="admin-expos">
      <PageTitle title={t('admin.expos.title')}>{t('admin.expos.desc')}</PageTitle>

      <div className="admin-expos__grid">
        <div className="admin-expos__list">
          <Card
            title={t('admin.expos.title')}
            description={t('admin.expos.listHint')}
            actions={
              <div className="card-filter">
                <Select
                  label={t('admin.expos.status')}
                  name="status"
                  value={status}
                  options={EXPO_STATUSES.map((s) => ({ value: s, label: s ? statusLabel(s) : t('action.all') }))}
                  onChange={setStatus}
                />
              </div>
            }
          >
            {expos.isLoading ? <LoadingState /> : null}
            {expos.error ? <ErrorState error={expos.error} onRetry={() => void expos.refetch()} /> : null}
            {expos.data ? (
              <DataTable<Expo>
                caption={t('admin.expos.title')}
                rows={expos.data.items ?? []}
                rowKey={(e) => e.expo_id}
                empty={<EmptyState>{t('admin.expos.empty')}</EmptyState>}
                columns={[
                  {
                    key: 'name',
                    header: t('table.name'),
                    cell: (e) => <Link className="table-primary-link" to={`/admin/expos/${e.expo_id}`}>{bilingual(e.name_ar, e.name_en)}</Link>,
                  },
                  { key: 'slug', header: t('admin.expos.slug'), cell: (e) => <Ltr>{e.slug}</Ltr> },
                  {
                    key: 'status',
                    header: t('table.status'),
                    cell: (e) => <Badge tone={statusTone(e.status)}>{statusLabel(e.status)}</Badge>,
                  },
                  { key: 'tz', header: t('admin.expos.timezone'), cell: (e) => <Ltr>{e.timezone}</Ltr> },
                  {
                    key: 'booths',
                    header: t('admin.expos.boothCounts'),
                    cell: (e) => <span className="numeral">{e.actual_booth_count} / {e.expected_booth_count}</span>,
                  },
                  { key: 'created', header: t('table.created'), cell: (e) => formatDateTime(e.created_at) },
                ]}
              />
            ) : null}
          </Card>
        </div>

        <aside className="admin-expos__create">
          <Card title={t('action.create')} description={t('admin.expos.createHint')}>
            <form
              className="create-expo-form"
              onSubmit={(ev) => {
                ev.preventDefault();
                setScheduleAttempted(true);
                const payload = schedulePayload(schedule, timezone);
                if (hasScheduleErrors || !payload) {
                  queueMicrotask(() => scheduleErrorRef.current?.focus());
                  return;
                }
                create.mutate(newIntent({
                  path: '/v1/admin/expos',
                  body: { slug, name_ar: nameAr, name_en: nameEn, timezone,
                    expected_booth_count: Number(expectedBoothCount), ...payload },
                }));
              }}
            >
              <Field label={t('admin.expos.slug')} name="slug" value={slug} onChange={setSlug} required ltr />
              <Field label={t('admin.expos.nameAr')} name="name_ar" value={nameAr} onChange={setNameAr} required />
              <Field label={t('admin.expos.nameEn')} name="name_en" value={nameEn} onChange={setNameEn} required ltr />
              <Field label={t('admin.expos.timezone')} name="timezone" value={timezone} onChange={setTimezone} required ltr />
              <Field label={t('admin.expos.expectedBooths')} hint={t('admin.expos.expectedBoothsHint')}
                name="expected_booth_count" type="number" value={expectedBoothCount}
                onChange={setExpectedBoothCount} required ltr />
              <div className="create-expo-form__schedule">
                <h3>{t('admin.expo.schedule.title')}</h3>
                <p>{t('admin.expo.schedule.createHint')}</p>
                {scheduleAttempted && hasScheduleErrors ? (
                  <div className="state state--error" role="alert" tabIndex={-1} ref={scheduleErrorRef}>
                    {t('admin.expo.schedule.error.summary')}
                  </div>
                ) : null}
                <ExpoScheduleFields
                  values={schedule}
                  onChange={setSchedule}
                  timezone={timezone}
                  errors={scheduleAttempted ? scheduleErrors : {}}
                />
              </div>
              {create.error ? <ErrorState error={create.error} /> : null}
              <button className="btn create-expo-form__submit" type="submit" disabled={create.isPending}>
                {t('action.create')}
              </button>
            </form>
          </Card>
        </aside>
      </div>
    </div>
  );
}
